<script lang="ts">
  import autosize from 'autosize'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { objectKeys } from '#app/lib/utils'
  import Spinner from '#general/components/spinner.svelte'
  import { askConfirmation } from '#general/lib/confirmation_modal'
  import VisibilitySelector from '#inventory/components/visibility_selector.svelte'
  import { listingTypeByEntitiesTypes, type ListingEntityType } from '#listings/lib/entities_typing'
  import { updateListing, deleteListing, createListing, serializeListing, type SerializedListing } from '#listings/lib/listings'
  import { showMainUserListings } from '#listings/listings'
  import { I18n, i18n } from '#user/lib/i18n'
  import { updateMainUserListingsCount } from '#user/lib/serialized_main_user'

  let {
    listing = $bindable(),
    layoutTitle = i18n('Edit list'),
    entityType,
    onListingEditorDone,
  }: {
    listing?: SerializedListing
    layoutTitle?: string
    entityType?: ListingEntityType
    onListingEditorDone?: (listing: SerializedListing) => void
  } = $props()

  let validating: Promise<void> = $state()
  let flash: FlashState = $state()
  const _id = $derived(listing?._id)
  let name = $derived(listing?.name)
  let description = $derived(listing?.description)
  let visibility = $derived(listing?.visibility)
  const listingTypesI18nKey = {
    work: 'work and series',
    author: 'P50',
    publisher: 'P123',
  }
  const listingTypes = objectKeys(listingTypesI18nKey)

  let type = $derived(listing?.type || listingTypeByEntitiesTypes[entityType] || 'work')

  async function validate () {
    validating = _validate()
  }

  async function _validate () {
    try {
      if (_id) {
        await _updateListing()
      } else {
        await _createListing()
      }
      onListingEditorDone?.(listing)
    } catch (err) {
      flash = err
    }
  }

  async function _updateListing () {
    await updateListing({
      id: _id,
      name,
      description,
      visibility,
    })
    listing = Object.assign(listing, { name, description, visibility })
  }

  async function _createListing () {
    const res = await createListing({
      name,
      description,
      visibility,
      type,
    })
    listing = serializeListing(res.listing)
    updateMainUserListingsCount(1)
  }

  async function askListDeletionConfirmation () {
    askConfirmation({
      confirmationText: I18n('delete_list_confirmation', { name }),
      warningText: I18n('cant_undo_warning'),
      action: _deleteListing,
    })
  }

  async function _deleteListing () {
    try {
      await deleteListing({ ids: [ _id ] })
      updateMainUserListingsCount(-1)
      await showMainUserListings()
    } catch (err) {
      flash = err
    }
  }
</script>

<div role="dialog" aria-labelledby="listing-editor-title">
  <h3 id="listing-editor-title">{layoutTitle}</h3>
  {#if !listing}
    <label>
      {i18n('List type')}
      <select
        name={i18n('List type selector')}
        bind:value={type}
        class="listing-type"
      >
        {#each listingTypes as selectableType (selectableType)}
          <option value={selectableType}>
            {I18n(listingTypesI18nKey[selectableType])}
          </option>
        {/each}
      </select>
    </label>
  {/if}
  <label>
    {i18n('name')}
    <input
      type="text"
      placeholder={i18n('List name')}
      bind:value={name}
      maxlength="128"
      required
    />
  </label>
  <label>
    {I18n('description')}
    <textarea
      type="text"
      bind:value={description}
      use:autosize
      maxlength="5000"
    ></textarea>
  </label>
  <div class="visibility-selector">
    <VisibilitySelector
      bind:visibility
      showTip={true}
    />
  </div>
  <div class="buttons">
    {#await validating}
      <Spinner />
    {:then}
      {#if listing}
        <button
          class="delete button"
          onclick={askListDeletionConfirmation}
        >
          {@html icon('trash')}
          {I18n('delete')}
        </button>
      {/if}
      <button
        class="validate button success-button"
        title={I18n('validate')}
        onclick={validate}
      >
        {@html icon('check')}
        {I18n('validate')}
      </button>
    {/await}
  </div>
  <Flash bind:state={flash} />
</div>

<style lang="scss">
  @import "general/scss/utils";
  h3{
    text-align: center;
  }
  label{
    font-size: 1rem;
    margin-block-end: 0.2em;
  }
  .visibility-selector, .listing-type{
    margin-block-end: 1em;
  }
  .buttons{
    @include display-flex(row, center, center);
    margin-block-start: 2em;
    margin-block-end: 1em;
  }
  button{
    min-width: 10rem;
    margin: 0 0.5em;
  }
  .delete{
    @include dangerous-action;
  }
</style>
